import React from 'react';
import {
  InboxIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  ArchiveBoxIcon,
  StarIcon,
  ArrowPathIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { EmailFolder } from '../../api/emailApi';

// Virtual folder for starred emails
export const STARRED_FOLDER = 'STARRED';

const folderConfig = [
  { id: EmailFolder.INBOX, name: 'Inbox', icon: InboxIcon, showUnread: true },
  { id: EmailFolder.SENT, name: 'Sent', icon: PaperAirplaneIcon },
  { id: EmailFolder.DRAFTS, name: 'Drafts', icon: DocumentTextIcon },
  { id: STARRED_FOLDER, name: 'Starred', icon: StarIcon, isVirtual: true },
  { id: EmailFolder.JUNK, name: 'Junk', icon: ExclamationTriangleIcon, showUnread: true },
  { id: EmailFolder.TRASH, name: 'Trash', icon: TrashIcon },
  { id: EmailFolder.ARCHIVE, name: 'Archive', icon: ArchiveBoxIcon },
];

/**
 * Desktop Sidebar - vertical layout (hidden on mobile)
 */
const EmailSidebar = ({
  selectedFolder,
  onSelectFolder,
  folderCounts,
  onCompose,
  onSync,
  syncing,
}) => {
  const renderFolderItem = (folder) => {
    const Icon = folder.icon;
    const isSelected = selectedFolder === folder.id;
    const count = folderCounts?.[folder.id.toLowerCase()] || 0;
    const unreadCount = folder.showUnread
      ? folderCounts?.[`${folder.id.toLowerCase()}Unread`] || 0
      : 0;

    return (
      <button
        key={folder.id}
        onClick={() => onSelectFolder(folder.id)}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
          isSelected
            ? 'bg-blue-100 text-blue-700'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="flex-1 text-left text-sm font-medium">{folder.name}</span>
        {unreadCount > 0 && (
          <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {unreadCount}
          </span>
        )}
        {!folder.showUnread && count > 0 && (
          <span className="text-gray-500 text-xs">{count}</span>
        )}
      </button>
    );
  };

  return (
    <div className="hidden lg:flex w-64 bg-gray-50 border-r border-gray-200 flex-col h-full">
      {/* Compose Button */}
      <div className="p-4">
        <button
          onClick={onCompose}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <PlusIcon className="h-5 w-5" />
          Compose
        </button>
      </div>

      {/* Folder List */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {folderConfig.map(renderFolderItem)}
      </nav>

      {/* Sync Button */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={onSync}
          disabled={syncing}
          className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors text-sm disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Emails'}
        </button>
      </div>
    </div>
  );
};

/**
 * Mobile Header - horizontal layout with compose button and folder tabs
 */
export const EmailMobileHeader = ({
  selectedFolder,
  onSelectFolder,
  folderCounts,
  onCompose,
  onSync,
  syncing,
}) => {
  const getUnreadCount = (folder) => {
    if (folder.showUnread) {
      return folderCounts?.[`${folder.id.toLowerCase()}Unread`] || 0;
    }
    return 0;
  };

  return (
    <div className="lg:hidden bg-white border-b border-gray-200">
      {/* Compose Button Row */}
      <div className="flex items-center gap-2 p-3">
        <button
          onClick={onCompose}
          className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <PlusIcon className="h-5 w-5" />
          Compose
        </button>
        <button
          onClick={onSync}
          disabled={syncing}
          className="p-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          title="Sync Emails"
        >
          <ArrowPathIcon className={`h-5 w-5 ${syncing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Horizontal Folder Tabs */}
      <div className="flex overflow-x-auto px-2 pb-2 gap-1 scrollbar-hide">
        {folderConfig.map((folder) => {
          const Icon = folder.icon;
          const isSelected = selectedFolder === folder.id;
          const unreadCount = getUnreadCount(folder);

          return (
            <button
              key={folder.id}
              onClick={() => onSelectFolder(folder.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full whitespace-nowrap transition-colors flex-shrink-0 ${
                isSelected
                  ? 'bg-blue-100 text-blue-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="text-sm">{folder.name}</span>
              {unreadCount > 0 && (
                <span className="bg-blue-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                  {unreadCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default EmailSidebar;

